import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PrefsProvider } from "@/context/PrefsContext";
import { AppShell } from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import { AssessmentIntro, AssessmentRun } from "@/pages/Assessment";
import AssessmentResult from "@/pages/AssessmentResult";
import AssessmentHistory from "@/pages/AssessmentHistory";
import StudyPlan from "@/pages/StudyPlan";
import ComingSoon from "@/pages/ComingSoon";
import SkillsAssessment from "@/pages/SkillsAssessment";
import Modules from "@/pages/Modules";
import ModulePage from "@/pages/ModulePage";
import { TestPage, TestResult } from "@/pages/Test";
import Settings from "@/pages/Settings";
import CareCenter from "@/pages/CareCenter";
import Guide from "@/pages/Guide";
import Admin from "@/pages/Admin";
import Partner, { PublicReport } from "@/pages/Partner";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) return <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground" data-testid="auth-loading">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarded && location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  if (user.onboarded && location.pathname === "/onboarding") return <Navigate to="/dashboard" replace />;
  return children;
}

function Shell({ children }) {
  const { user } = useAuth();
  if (user?.role === "institution_admin") return <Navigate to="/partner" replace />;
  return <Protected><AppShell>{children}</AppShell></Protected>;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "superadmin") return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to={user.role === "institution_admin" ? "/partner" : user.onboarded ? "/dashboard" : "/onboarding"} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/dashboard" element={<Shell><Dashboard /></Shell>} />
      <Route path="/assessment" element={<Shell><AssessmentIntro /></Shell>} />
      <Route path="/assessment/run" element={<Shell><AssessmentRun /></Shell>} />
      <Route path="/assessment/result" element={<Shell><AssessmentResult /></Shell>} />
      <Route path="/assessment/skills" element={<Shell><SkillsAssessment /></Shell>} />
      <Route path="/assessment/history" element={<Shell><AssessmentHistory /></Shell>} />
      <Route path="/study-plan" element={<Shell><StudyPlan /></Shell>} />
      <Route path="/modules" element={<Shell><Modules /></Shell>} />
      <Route path="/modules/english" element={<Shell><ComingSoon page="english" /></Shell>} />
      <Route path="/skills" element={<Shell><Modules track="skills" /></Shell>} />
      <Route path="/skills/caregiver" element={<Shell><Modules track="skills" plan="KAIGO" /></Shell>} />
      <Route path="/skills/caregiver-l2" element={<Shell><Modules track="skills" plan="KAIGO2" /></Shell>} />
      <Route path="/skills/agriculture" element={<Shell><Modules track="skills" plan="NOGYO" /></Shell>} />
      <Route path="/skills/logistics" element={<Shell><ComingSoon page="logistics" /></Shell>} />
      <Route path="/modules/:id" element={<Shell><ModulePage /></Shell>} />
      <Route path="/test/:moduleId" element={<Shell><TestPage /></Shell>} />
      <Route path="/test/result/:attemptId" element={<Shell><TestResult /></Shell>} />
      <Route path="/settings" element={<Shell><Settings /></Shell>} />
      <Route path="/care-center" element={<Shell><CareCenter /></Shell>} />
      <Route path="/guide" element={<Shell><Guide /></Shell>} />
      <Route path="/admin" element={<Shell><AdminOnly><Admin /></AdminOnly></Shell>} />
      <Route path="/partner" element={<Partner />} />
      <Route path="/laporan/:token" element={<PublicReport />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PrefsProvider>
            <AppRouter />
            <Toaster position="top-center" richColors closeButton />
          </PrefsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
